<template>
  <teleport to="body">
    <transition
      name="modal"
      appear
    >
      <div
        v-if="modelValue"
        class="base-modal"
        @click.self="handleBackdropClick"
      >
        <div
          :class="[
            'base-modal__dialog',
            `base-modal__dialog--${size}`
          ]"
          role="dialog"
          :aria-labelledby="titleId"
          aria-modal="true"
        >
          <header v-if="$slots.header || title" class="base-modal__header">
            <slot name="header">
              <h2 :id="titleId" class="base-modal__title">
                {{ title }}
              </h2>
              <button
                v-if="showClose"
                class="base-modal__close"
                type="button"
                aria-label="Close modal"
                @click="handleClose"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </slot>
          </header>

          <div class="base-modal__body">
            <slot name="body">
              <slot />
            </slot>
          </div>

          <footer v-if="$slots.footer" class="base-modal__footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { defineComponent, computed, watch, nextTick } from 'vue'

let modalIdCounter = 0

export default defineComponent({
  name: 'BaseModal',
  emits: ['update:modelValue', 'close', 'open'],
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
    },
    showClose: {
      type: Boolean,
      default: true
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    },
    closeOnEscape: {
      type: Boolean,
      default: true
    },
    persistent: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const titleId = computed(() => `modal-title-${++modalIdCounter}`)

    const handleClose = () => {
      if (!props.persistent) {
        emit('update:modelValue', false)
        emit('close')
      }
    }

    const handleBackdropClick = () => {
      if (props.closeOnBackdrop && !props.persistent) {
        handleClose()
      }
    }

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && props.closeOnEscape && props.modelValue && !props.persistent) {
        handleClose()
      }
    }

    // Handle body scroll lock
    const lockBodyScroll = () => {
      document.body.style.overflow = 'hidden'
    }

    const unlockBodyScroll = () => {
      document.body.style.overflow = ''
    }

    // Watch for modal open/close
    watch(() => props.modelValue, (isOpen) => {
      if (isOpen) {
        nextTick(() => {
          lockBodyScroll()
          emit('open')
        })
      } else {
        unlockBodyScroll()
      }
    }, { immediate: true })

    // Setup keyboard listeners
    watch(() => props.modelValue, (isOpen) => {
      if (isOpen) {
        document.addEventListener('keydown', handleEscapeKey)
      } else {
        document.removeEventListener('keydown', handleEscapeKey)
      }
    })

    return {
      titleId,
      handleClose,
      handleBackdropClick
    }
  }
})
</script>

<style scoped>
.base-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: var(--space-4);
}

.base-modal__dialog {
  background-color: var(--color-white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-2xl);
  max-height: calc(100vh - 2rem);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Modal Sizes */
.base-modal__dialog--sm {
  width: 100%;
  max-width: 400px;
}

.base-modal__dialog--md {
  width: 100%;
  max-width: 500px;
}

.base-modal__dialog--lg {
  width: 100%;
  max-width: 700px;
}

.base-modal__dialog--xl {
  width: 100%;
  max-width: 900px;
}

.base-modal__dialog--full {
  width: calc(100vw - 2rem);
  height: calc(100vh - 2rem);
  max-width: none;
  max-height: none;
}

/* Header */
.base-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-6);
  border-bottom: 1px solid var(--color-gray-200);
  background-color: var(--color-gray-50);
  flex-shrink: 0;
}

.base-modal__title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-900);
  margin: 0;
  line-height: 1.4;
}

.base-modal__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background-color: transparent;
  color: var(--color-gray-500);
  border-radius: var(--radius-base);
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.base-modal__close:hover {
  background-color: var(--color-gray-100);
  color: var(--color-gray-700);
}

.base-modal__close:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* Body */
.base-modal__body {
  flex: 1;
  padding: var(--space-6);
  overflow-y: auto;
  color: var(--color-gray-700);
  line-height: 1.6;
}

/* Footer */
.base-modal__footer {
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--color-gray-200);
  background-color: var(--color-gray-50);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  flex-shrink: 0;
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity var(--transition-base);
}

.modal-enter-active .base-modal__dialog,
.modal-leave-active .base-modal__dialog {
  transition: all var(--transition-base);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .base-modal__dialog,
.modal-leave-to .base-modal__dialog {
  opacity: 0;
  transform: scale(0.9) translateY(-50px);
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .base-modal {
    padding: var(--space-2);
    align-items: flex-start;
    padding-top: var(--space-4);
  }

  .base-modal__dialog {
    width: 100%;
    max-height: calc(100vh - 1rem);
  }

  .base-modal__dialog--sm,
  .base-modal__dialog--md,
  .base-modal__dialog--lg,
  .base-modal__dialog--xl {
    max-width: none;
  }

  .base-modal__header {
    padding: var(--space-4);
  }

  .base-modal__body {
    padding: var(--space-4);
  }

  .base-modal__footer {
    padding: var(--space-3) var(--space-4);
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .base-modal__footer > * {
    width: 100%;
  }

  .base-modal__title {
    font-size: var(--font-size-lg);
  }
}

/* Tablet adjustments */
@media (max-width: 1023px) {
  .base-modal__dialog--lg,
  .base-modal__dialog--xl {
    max-width: calc(100vw - 2rem);
  }
}

/* Focus trap styling */
.base-modal__dialog:focus {
  outline: none;
}

/* Scrollbar styling for modal body */
.base-modal__body::-webkit-scrollbar {
  width: 6px;
}

.base-modal__body::-webkit-scrollbar-track {
  background: var(--color-gray-100);
}

.base-modal__body::-webkit-scrollbar-thumb {
  background: var(--color-gray-300);
  border-radius: 3px;
}

.base-modal__body::-webkit-scrollbar-thumb:hover {
  background: var(--color-gray-400);
}
</style>